import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileSignature, Send } from "lucide-react";
import { useEsignSetup, createEsignRequest } from "@/components/esign/useEsignSetup";
import SignatorySelect from "@/components/esign/SignatorySelect";
import ESignRequestStatus from "@/components/esign/ESignRequestStatus";

/** Aadhaar eSign any generated PDF. `pdfPath` is an /api-relative URL that returns the PDF. */
export default function ESignButton({ pdfPath, fileName, docType = "document", docRef = "", title = "", size = "sm", testId = "esign-open-btn" }) {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState("");
  const [req, setReq] = useState(null);
  const [busy, setBusy] = useState(false);
  const { config, signatories } = useEsignSetup(open);

  const loadExisting = useCallback(async () => {
    if (!docRef) { return; }
    const r = await api.get("/esign/requests", { params: { doc_type: docType, doc_ref: docRef } });
    setReq((r.data || []).find((x) => x.status !== "cancelled") || null);
  }, [docType, docRef]);

  useEffect(() => {
    if (open) { loadExisting().catch(() => {}); }
  }, [open, loadExisting]);

  const send = async () => {
    setBusy(true);
    try {
      const pdf = await api.get(pdfPath, { responseType: "blob" });
      const d = await createEsignRequest({ blob: pdf.data, fileName, signatoryId: sid, docType, docRef, title });
      setReq(d);
      toast.success("eSign request created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create eSign request");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant="outline" onClick={() => setOpen(true)} className="rounded-sm gap-1.5" data-testid={testId}>
        <FileSignature className="w-3.5 h-3.5" /> Aadhaar eSign
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="esign-dialog">
          <DialogHeader>
            <DialogTitle>Aadhaar eSign · {title || fileName}</DialogTitle>
            <DialogDescription>Signer approves with an OTP on their Aadhaar-linked mobile. No USB token needed.</DialogDescription>
          </DialogHeader>
          {config && !config.configured && (
            <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-2" data-testid="esign-mock-banner">
              SANDBOX MOCK mode. Digio keys not added yet, so signatures are simulated and not legally valid.
            </div>
          )}
          {req ? (
            <div className="space-y-3">
              <ESignRequestStatus request={req} onChange={setReq} mockOtpHint={config?.mock_otp_hint} />
              {req.status === "completed" && (
                <Button size="sm" variant="outline" onClick={() => setReq(null)} className="rounded-sm" data-testid="esign-sign-again-btn">
                  Sign again (revised document)
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <SignatorySelect signatories={signatories} value={sid} onChange={setSid} />
              <Button onClick={send} disabled={!sid || busy} className="rounded-sm gap-1.5 w-full" data-testid="esign-send-btn">
                <Send className="w-3.5 h-3.5" /> {busy ? "Preparing PDF…" : "Send for Aadhaar eSign"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
